import React from 'react';

import { SimpleSailingDay, SimpleSailing } from '@go-shared/types/sailing';

import './CalendarViewDay.scss';
import TemplateGlanceButton from '@go-client/components/TemplateGlanceButton';
import { BubbleButton } from '@go-client/components/BubbleUI';
import { setStorage } from '@go-client/lib/storage';
import { resolvePlannerPath } from '@go-client/routes';

interface CalendarViewDayProps {
  day: SimpleSailingDay;
  sailing: SimpleSailing;
  selectedMonth: number;
  onContentBuilderClick: (sailing: SimpleSailing, sailingDay: SimpleSailingDay) => void;
  onRundownClick: (sailing: SimpleSailing, sailingDay: SimpleSailingDay) => void;
  onSailingDayLock: (sailing: SimpleSailing, sailingDay: SimpleSailingDay) => void;
  onSailingDayStar: (sailing: SimpleSailing, sailingDay: SimpleSailingDay) => void;
  onSailingDayTemplateClick: (sailing: SimpleSailing, sailingDay: SimpleSailingDay) => void;
}

/**
 * Helper to determine if the current day is the same as the passed UTC date. Note that we cannot
 * use `isSameDay` or `isSameDayUTC` because we are comparing a local date with a UTC one
 *
 */
function isCurrentDay(utcDate: Date): boolean {
  let now = new Date();

  return (
    now.getFullYear() === utcDate.getUTCFullYear() &&
    now.getMonth() === utcDate.getUTCMonth() &&
    now.getDate() === utcDate.getUTCDate()
  );
}

export const CalendarViewDay: React.FC<CalendarViewDayProps> = ({
  sailing,
  day,
  onContentBuilderClick,
  onRundownClick,
  selectedMonth,
  onSailingDayLock,
  onSailingDayTemplateClick,
}) => {
  const sameMonth = selectedMonth === day.date.getUTCMonth();
  const sameDay = sameMonth && isCurrentDay(day.date);

  if (sameDay) {
    setStorage('currentSailingUrl', resolvePlannerPath(sailing.property._id, sailing._id, day._id));
  }

  return (
    <div
      className={`CalendarDay bg-white hover:bg-white-dark relative cursor-pointer ${
        sameDay ? 'sameCalendarDay' : ''
      } ${sameMonth ? '' : 'pointer-events-none'}`}
      style={{ width: 100 / 7 + '%', paddingBottom: 70 / 7 + '%' }}
      onClick={() => onRundownClick(sailing, day)}
    >
      <div className="absolute w-full h-full flex flex-col">
        {sameMonth && (
          <>
            <div className="flex items-start justify-between">
              <h3 className="font-bold text-navy p-2">{day.date.getUTCDate()}</h3>
              <div className="p-2">
                <TemplateGlanceButton
                  locked={day.locked}
                  onClick={() => {
                    onSailingDayTemplateClick(sailing, day);
                  }}
                  className="p-2"
                  templateFragments={day.templateFragments}
                  templateHistory={day.templateHistory}
                />
              </div>
            </div>
            <div className="mt-auto p-1 text-right">
              <span className={day.locked ? '' : 'hiddenActions'}>
                <BubbleButton
                  className={`${day.locked ? 'text-crimson' : 'text-smoke'} p-1-i`}
                  inverse
                  variant={day.locked ? 'crimson' : 'smoke'}
                  onClick={() => {
                    onSailingDayLock(sailing, day);
                  }}
                  icon={day.locked ? 'lock' : 'unlock'}
                />
              </span>
              <BubbleButton
                variant="navy"
                inverse
                className="p-1-i"
                onClick={() => {
                  onContentBuilderClick(sailing, day);
                }}
                icon={['fass', 'newspaper']}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
